import type { AiHelpMessage, HighlightInstruction, StepInstruction } from '@buyerflo/core/schemas/aiHelp'
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'

interface AiHelpState {
    isOpen: boolean
    isExpanded: boolean
    messages: AiHelpMessage[]
    highlights: HighlightInstruction[]
    steps: StepInstruction[]
    completedSteps: number[]
    isLoading: boolean
    currentStepIndex: number
    isInStepFlow: boolean
    conversationId: string
}

interface AiHelpActions {
    setIsOpen: (open: boolean) => void
    setIsExpanded: (expanded: boolean) => void
    addUserMessage: (content: string) => void
    addAssistantMessage: (msg: AiHelpMessage) => void
    setHighlights: (highlights: HighlightInstruction[]) => void
    clearHighlights: () => void
    setSteps: (steps: StepInstruction[]) => void
    completeStep: (stepNumber: number) => void
    clearChat: () => void
    setIsLoading: (loading: boolean) => void
    advanceStep: () => void
    exitStepFlow: () => void
    setConversationId: (id: string) => void
    hydrateMessages: (msgs: AiHelpMessage[]) => void
}

type AiHelpContextType = AiHelpState & AiHelpActions

const AiHelpCtx = createContext<AiHelpContextType | null>(null)

export function useAiHelp() {
    const ctx = useContext(AiHelpCtx)
    if (!ctx) throw new Error('useAiHelp must be used within AiHelpProvider')
    return ctx
}

export function AiHelpProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [messages, setMessages] = useState<AiHelpMessage[]>([])
    const [highlights, setHighlights] = useState<HighlightInstruction[]>([])
    const [steps, setSteps] = useState<StepInstruction[]>([])
    const [completedSteps, setCompletedSteps] = useState<number[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [currentStepIndex, setCurrentStepIndex] = useState(-1)
    const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID())

    // Use ref so advanceStep always sees latest steps without re-creating
    const stepsRef = useRef(steps)
    stepsRef.current = steps

    const highlightStep = useCallback((step: StepInstruction) => {
        if (step.helpId) {
            setHighlights([{ helpId: step.helpId, label: step.description, action: step.action }])
        } else {
            setHighlights([])
        }
    }, [])

    const addUserMessage = useCallback((content: string) => {
        setMessages(prev => [...prev, { role: 'user', content }])
    }, [])

    const addAssistantMessage = useCallback((msg: AiHelpMessage) => {
        setMessages(prev => [...prev, msg])
        if (msg.steps?.length) {
            setSteps(msg.steps)
            setCompletedSteps([])
            setCurrentStepIndex(0)
            stepsRef.current = msg.steps
            // Auto-highlight first step's element
            const firstStep = msg.steps[0]
            if (firstStep.helpId) {
                setHighlights([{ helpId: firstStep.helpId, label: firstStep.description, action: firstStep.action }])
            }
            // Collapse so user sees the step bar
            setIsExpanded(false)
        } else {
            if (msg.highlights?.length) setHighlights(msg.highlights)
        }
    }, [])

    const clearHighlights = useCallback(() => {
        setHighlights([])
    }, [])

    const completeStep = useCallback((stepNumber: number) => {
        setCompletedSteps(prev => (prev.includes(stepNumber) ? prev : [...prev, stepNumber]))
    }, [])

    const exitStepFlow = useCallback(() => {
        setCurrentStepIndex(-1)
        setSteps([])
        setCompletedSteps([])
        setHighlights([])
    }, [])

    const advanceStep = useCallback(() => {
        const allSteps = stepsRef.current
        setCurrentStepIndex(prev => {
            if (prev < 0) return prev
            // Mark current step completed
            const currentStep = allSteps[prev]
            if (currentStep) {
                setCompletedSteps(cs =>
                    cs.includes(currentStep.stepNumber) ? cs : [...cs, currentStep.stepNumber],
                )
            }
            const nextIndex = prev + 1
            if (nextIndex < allSteps.length) {
                // Highlight next step
                const nextStep = allSteps[nextIndex]
                if (nextStep?.helpId) {
                    setHighlights([{ helpId: nextStep.helpId, label: nextStep.description, action: nextStep.action }])
                } else {
                    setHighlights([])
                }
                return nextIndex
            } else {
                // All steps done
                setHighlights([])
                setSteps([])
                setCompletedSteps([])
                return -1
            }
        })
    }, [])

    const hydrateMessages = useCallback((msgs: AiHelpMessage[]) => {
        setMessages(msgs)
    }, [])

    const clearChat = useCallback(() => {
        setMessages([])
        setHighlights([])
        setSteps([])
        setCompletedSteps([])
        setCurrentStepIndex(-1)
        setConversationId(crypto.randomUUID())
    }, [])

    const value = useMemo(
        () => ({
            isOpen,
            isExpanded,
            messages,
            highlights,
            steps,
            completedSteps,
            isLoading,
            currentStepIndex,
            isInStepFlow: currentStepIndex >= 0 && steps.length > 0,
            conversationId,
            setIsOpen,
            setIsExpanded,
            addUserMessage,
            addAssistantMessage,
            setHighlights,
            clearHighlights,
            setSteps,
            completeStep,
            clearChat,
            setIsLoading,
            advanceStep,
            exitStepFlow,
            setConversationId,
            hydrateMessages,
        }),
        [
            isOpen,
            isExpanded,
            messages,
            highlights,
            steps,
            completedSteps,
            isLoading,
            currentStepIndex,
            conversationId,
            addUserMessage,
            addAssistantMessage,
            clearHighlights,
            completeStep,
            clearChat,
            advanceStep,
            exitStepFlow,
            hydrateMessages,
        ],
    )

    return <AiHelpCtx.Provider value={value}>{children}</AiHelpCtx.Provider>
}
